import type * as React from 'react';
import { Link } from 'react-router-dom';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@dfq/shared/lib/utils';

/**
 * Variants for the Footer surface.
 *
 * - `muted` (default) — light surface for non-storefront contexts; uses
 *   `bg-muted-surface` with a top divider.
 * - `page` — page background, used when stacking the footer below an
 *   already-muted section.
 * - `dark` — dark charcoal storefront chrome (Figma `43:103`). No top
 *   divider; column headings render in `text-on-primary` and links in
 *   `text-storefront-chrome-muted`.
 */
const footerVariants = cva('w-full', {
  variants: {
    variant: {
      muted: 'border-t border-divider bg-muted-surface',
      page: 'border-t border-divider bg-page',
      dark: 'bg-storefront-chrome border-t-0',
    },
  },
  defaultVariants: { variant: 'muted' },
});

export type FooterVariant = NonNullable<VariantProps<typeof footerVariants>['variant']>;

/**
 * Inner-container wrapper variants. Width is hard-wired to `max-w-screen-xl`
 * so the footer tracks the same gutter rhythm as the rest of the storefront
 * page content; only vertical padding changes per variant.
 */
const footerContainerVariants = cva(
  'mx-auto flex w-full max-w-screen-xl flex-col gap-8 px-4 sm:px-6 lg:px-8',
  {
    variants: {
      padY: {
        dark: 'pt-14 pb-8',
        default: 'py-12',
      },
    },
    defaultVariants: { padY: 'default' },
  },
);

export interface FooterLink {
  /** Visible label. */
  label: string;
  /** `react-router-dom` destination (relative or absolute path). */
  to: string;
}

export interface FooterColumn {
  /** Column heading (e.g. `Company`, `Products`). */
  title: string;
  /** Ordered list of links under the heading. */
  links: FooterLink[];
}

export interface FooterSocialLink {
  /** Icon node — typically a `lucide-react` icon (`<Instagram />`, `<Twitter />`). */
  icon: React.ReactNode;
  /** Absolute URL to the social profile. Rendered via `<a>` with `target="_blank"`. */
  href: string;
  /** Accessible label (e.g. `"DFQ Printing on Instagram"`). Required for a11y. */
  label: string;
}

export interface FooterBrand {
  /** Wordmark rendered as the column header. Defaults to brand wordmark. */
  wordmark?: React.ReactNode;
  /** Multi-line tagline below the wordmark (e.g. "Wholesale Vehicle Wrap Printing"). */
  tagline?: React.ReactNode;
  /** Location line (e.g. "Las Vegas, NV"). */
  location?: React.ReactNode;
}

export interface FooterContact {
  /** Right-aligned contact line on the bottom strip — typically `City · Phone · Email`. */
  text?: React.ReactNode;
}

export interface FooterProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'title'>,
    VariantProps<typeof footerVariants> {
  /** Up to four link columns. On `< md` they stack; on `>= md` they render as a multi-col grid. */
  columns: FooterColumn[];
  /** Optional social icon cluster shown on the right side of the bottom strip. */
  socialLinks?: FooterSocialLink[];
  /** Override the brand label used in the copyright line. Defaults to `DFQ Printing`. */
  brandName?: string;
  /**
   * Optional brand block rendered as the first column when present. When
   * supplied (typically with `variant="dark"`), the first three entries of
   * `columns` render in the remaining three columns.
   */
  brand?: FooterBrand;
  /** Optional contact line rendered on the right side of the bottom strip. */
  contact?: FooterContact;
  /**
   * Full copyright line override (e.g. the admin-configured
   * `"© 2026 DFQ Printing. All rights reserved."` from SF-NAV-101). Takes
   * precedence over the auto-computed `© {currentYear} {brandName}` line.
   * The caller is responsible for resolving any `{year}` placeholder before
   * passing this down — the capsule renders it verbatim.
   */
  copyrightText?: React.ReactNode;
}

export function Footer({
  columns,
  socialLinks,
  brandName = 'DFQ Printing',
  variant,
  brand,
  contact,
  copyrightText,
  className,
  ...rest
}: FooterProps) {
  const currentYear = new Date().getFullYear();
  const isDark = variant === 'dark';

  // Derived per-variant class strings — keeps the JSX legible vs. scattering
  // ternaries on every node.
  const headingClass = isDark
    ? 'text-on-primary text-body-default font-semibold'
    : 'text-body-default font-semibold text-text-primary';
  const linkClass = isDark
    ? 'text-body-default text-storefront-chrome-muted transition-colors hover:text-on-primary'
    : 'text-body-default text-text-secondary transition-colors hover:text-text-primary';
  const dividerClass = isDark ? 'border-storefront-chrome-divider' : 'border-divider';
  const bottomTextClass = isDark
    ? 'text-body-small text-storefront-chrome-fine'
    : 'text-body-small text-text-secondary';

  // When a brand block is provided (dark variant), the first column is the
  // brand and the next three are the first three entries of `columns`.
  const renderBrandColumn = brand !== undefined;
  const visibleColumns = renderBrandColumn ? columns.slice(0, 3) : columns;

  // Dark variant uses a flex layout with a stretching brand block + fixed
  // 100px link columns (Figma `43:104`). Other variants keep the legacy
  // 4-col grid for backwards compat with existing consumers.
  const columnsContainerClass = isDark
    ? 'flex flex-col gap-8 md:flex-row md:flex-wrap md:items-start md:gap-16'
    : 'grid grid-cols-1 gap-8 md:grid-cols-4';

  return (
    <footer className={cn(footerVariants({ variant }), className)} {...rest}>
      <div
        className={footerContainerVariants({
          padY: isDark ? 'dark' : 'default',
        })}
      >
        <div className={columnsContainerClass}>
          {renderBrandColumn ? (
            <div className="flex flex-col gap-3 md:flex-1">
              {brand.wordmark !== undefined ? (
                <p className="text-on-primary text-base font-semibold tracking-[1.6px]">
                  {brand.wordmark}
                </p>
              ) : null}
              {brand.tagline !== undefined || brand.location !== undefined ? (
                <div className="text-body-small text-storefront-chrome-muted leading-[1.7]">
                  {brand.tagline !== undefined ? <p>{brand.tagline}</p> : null}
                  {brand.location !== undefined ? <p>{brand.location}</p> : null}
                </div>
              ) : null}
            </div>
          ) : null}
          {visibleColumns.map((column) => (
            <div
              key={column.title}
              className={cn(
                'flex flex-col gap-3',
                isDark && 'md:w-[100px] md:shrink-0',
              )}
            >
              <h2 className={headingClass}>{column.title}</h2>
              <ul className="flex flex-col gap-2">
                {column.links.map((link) => (
                  <li key={`${column.title}-${link.label}`}>
                    <Link to={link.to} className={linkClass}>
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div
          className={cn(
            'flex flex-col items-start justify-between gap-4 border-t pt-6 md:flex-row md:items-center',
            dividerClass,
          )}
        >
          <span className={bottomTextClass}>
            {copyrightText ?? (
              <>
                &copy; {currentYear} {brandName}
              </>
            )}
          </span>
          {socialLinks && socialLinks.length > 0 ? (
            <ul className="flex items-center gap-4">
              {socialLinks.map((social) => (
                <li key={social.label}>
                  <a
                    href={social.href}
                    aria-label={social.label}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      'inline-flex size-9 items-center justify-center rounded-md transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                      isDark
                        ? 'text-storefront-chrome-muted hover:text-on-primary'
                        : 'text-text-secondary hover:text-text-primary',
                    )}
                  >
                    <span aria-hidden="true" className="inline-flex">
                      {social.icon}
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          ) : null}
          {contact?.text !== undefined ? (
            <span className={cn(bottomTextClass, 'md:text-right')}>{contact.text}</span>
          ) : null}
        </div>
      </div>
    </footer>
  );
}
